import React, { useEffect, useMemo, useState } from "react";
import ApiClient from "../api/ApiClient";
import { useAuth } from "../contexts/AuthContext";
import { ModelShares } from "./ModelShares";
import WarningsModal from "./admin/dialogs/WarningsModal";
import "./ModelSpreadsheet.scss";

// Placeholder the API returns in place of a real formula for non-admin
// callers. Recognisable but inert — kept in sync with
// <HiddenFormulaPlaceholder> on SpreadsheetController.
const HIDDEN_FORMULA_PLACEHOLDER = "<Hidden formula>";

// Convert a 0xAARRGGBB ARGB value from Constants.ColorTable into a CSS rgb() string.
function colorToCss(color) {
  if (color == null) return null;
  const argb = Number(color);
  if (!Number.isFinite(argb)) return null;
  const r = (argb >> 16) & 0xff;
  const g = (argb >> 8) & 0xff;
  const b = argb & 0xff;
  return `rgb(${r}, ${g}, ${b})`;
}

const pageSizeOptions = [25, 50, 100, 500];

export const ModelSpreadsheet = () => {
  // Server hides formulas from non-admins (returns "<Hidden formula>" in
  // place of the real text). Mirror that here so the column-header
  // tooltip — which is rendered from the same payload — never includes
  // the formula either. The placeholder check is intentionally strict
  // ("<Hidden formula>" exactly) so a typo on the server side would be
  // caught rather than silently shown.
  const { user, isAuthenticated } = useAuth();
  const isAdmin = isAuthenticated && !!user?.Roles?.includes("Admin");

  const [models, setModels] = useState([]);
  const [selectedModelId, setSelectedModelId] = useState("");

  const [computing, setComputing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  // Persisted calculation metadata (so the user can see "last run" info).
  const [currentCalculationId, setCurrentCalculationId] = useState(null);

  // Warnings count for the currently rendered calculation. Sourced from
  // the calculate/recent endpoints (slim header carries WarningsCount);
  // the full warnings list itself is fetched lazily by the modal via
  // GET /api/spreadsheets/{id}/warnings.
  const [resultWarningsCount, setResultWarningsCount] = useState(0);

  // Modal state for the lazy-fetched warnings dialog.
  const [warningsModal, setWarningsModal] = useState(null);

  // Recent calculations list (max 5 most recent for the selected model).
  const [recentCalculations, setRecentCalculations] = useState([]);
  const [loadingRecent, setLoadingRecent] = useState(false);
  const [selectedRecentId, setSelectedRecentId] = useState(null);

  // Track which calculation id is currently being deleted so the row's
  // Delete button can show a spinner and disable itself during the call.
  const [deletingRecentId, setDeletingRecentId] = useState(null);

  // Separate spinner state for the toolbar "Delete edited copy" button —
  // the recent-calculations list and the toolbar can show their own
  // independent busy indicators.
  const [deletingEditedId, setDeletingEditedId] = useState(false);

  // Pagination state. Page size is selectable per the requirement:
  // 25 / 50 (default) / 100 / 500.
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);

  // Cell editing state
  //
  // Edits are buffered locally until the user clicks Save:
  //   * `pendingEdits` — Map<"col:row", { originalText, newValue }> of
  //     cells the user has typed into but not yet committed. Rendered
  //     with the orange highlight.
  //   * `persistedEditedCells` — Dictionary<"col:row", newValue> lifted
  //     from the response payload of the loaded calculation. Drives the
  //     yellow "persisted edit" highlight (only meaningful on the edited
  //     sibling row).
  //   * `editingCell` / `editingValue` — the row/col/value of the cell
  //     currently in the <input> editor. Same UX as before — only the
  //     persistence path changed.
  //   * `savingAll` — true while the bulk Save is in flight; applied
  //     to every cell on the page so the user sees a uniform busy
  //     indicator rather than per-cell spinners.
  //   * `editError` — surfaced if Save fails.
  const [pendingEdits, setPendingEdits] = useState(new Map()); // "col:row" -> { originalText, newValue }
  const [persistedEditedCells, setPersistedEditedCells] = useState({}); // "col:row" -> newValue
  const [editingCell, setEditingCell] = useState(null); // { rowIdx, colIdx }
  const [editingValue, setEditingValue] = useState("");
  const [savingAll, setSavingAll] = useState(false);
  const [editError, setEditError] = useState(null);

  // Edited-version toggle: when a user has a saved edited sibling for
  // the currently-loaded original, they can flip into it to see the
  // recomputed grid. The edited calc id is remembered separately so we
  // can drive the Original/Edited segmented control.
  const [editedCalcAvailable, setEditedCalcAvailable] = useState(false); // does the user have an edited sibling?
  const [viewMode, setViewMode] = useState("original"); // "original" | "edited"

  // Edited view filter: when true, only render rows that contain at
  // least one persisted or pending edit. Useful for reviewing a long
  // grid that only diverges from the original in a handful of rows.
  const [modifiedRowsOnly, setModifiedRowsOnly] = useState(false);

  // Sorted visible rows for the current page.
  //
  // `result.Rows` always contains the rows for the *current* page only —
  // fetchPage replaces rather than accumulates (see below). We just need a
  // stable sort; no slicing. The previous "accumulate-then-slice" logic
  // was broken: visiting page 4 after page 0 left Rows = [page0, page4],
  // and visibleRows = sorted.slice(200, 250) on a 100-element array
  // returned []. AdminCompareSpreadsheets' "Show all" panel uses the
  // same simple render-rows-as-they-come approach.
  //
  // When `modifiedRowsOnly` is on (only meaningful on the edited view),
  // we additionally filter the page to rows that have at least one
  // persisted or pending edit. The page count and totals don't change
  // — we still paginate the full grid server-side and filter
  // client-side on each page.
  const visibleRows = useMemo(() => {
    if (!result?.Rows) return [];
    const sorted = [...result.Rows].sort(
      (a, b) => (a.Index ?? 0) - (b.Index ?? 0)
    );
    if (!modifiedRowsOnly) return sorted;

    const persistedKeys = new Set(Object.keys(persistedEditedCells || {}));
    return sorted.filter((row) => {
      const rowIndex = row.Index;
      const cells = row.Cells || [];
      for (let ci = 0; ci < cells.length; ci++) {
        const colIdx = ci; // Cells are aligned with column index order.
        if (pendingEdits.has(`${colIdx}:${rowIndex}`)) return true;
        if (persistedKeys.has(`${colIdx}:${rowIndex}`)) return true;
      }
      return false;
    });
  }, [result, modifiedRowsOnly, pendingEdits, persistedEditedCells]);

  const totalRows = result?.RowCount ?? result?.Rows?.length ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalRows / pageSize));

  // Local state for the "jump to page" input. Kept in sync with the
  // current page so users always see where they are, but a draft value
  // is allowed while typing so the field doesn't fight the user mid-edit.
  const [pageInputValue, setPageInputValue] = useState(String(page + 1));
  useEffect(() => {
    setPageInputValue(String(page + 1));
  }, [page]);

  function commitPageInput() {
    const parsed = parseInt(pageInputValue, 10);
    if (!Number.isFinite(parsed)) {
      setPageInputValue(String(page + 1));
      return;
    }
    const target = Math.min(totalPages, Math.max(1, parsed));
    setPageInputValue(String(target));
    fetchPage(target - 1);
  }

  useEffect(() => {
    loadModels();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When the selected model changes, refresh the recent-calculations list
  // and reset the spreadsheet view.
  useEffect(() => {
    if (selectedModelId) {
      loadRecentCalculations(parseInt(selectedModelId, 10));
    } else {
      setRecentCalculations([]);
      setSelectedRecentId(null);
    }
    setResult(null);
    setCurrentCalculationId(null);
    setError(null);
    setPage(0);
    setResultWarningsCount(0);
    setWarningsModal(null);
    setPendingEdits(new Map());
    setPersistedEditedCells({});
    setEditingCell(null);
    setEditingValue("");
    setEditError(null);
    setEditedCalcAvailable(false);
    setViewMode("original");
    setModifiedRowsOnly(false);
  }, [selectedModelId]);

  async function loadModels() {
    try {
      const list = await ApiClient.getModelsList();
      setModels(list || []);
      if ((list || []).length > 0 && !selectedModelId) {
        setSelectedModelId(list[0].Id.toString());
      }
    } catch (err) {
      console.error("Failed to load models", err);
      setModels([]);
    }
  }

  async function loadRecentCalculations(modelId) {
    if (!modelId) return;
    setLoadingRecent(true);
    try {
      const data = await ApiClient.getRecentModelSpreadsheets(modelId, 5);
      setRecentCalculations(data?.Items || []);
    } catch (err) {
      console.error("Failed to load recent spreadsheets", err);
      setRecentCalculations([]);
    } finally {
      setLoadingRecent(false);
    }
  }

  async function calculateNew() {
    if (!selectedModelId) {
      alert("Please select a model");
      return;
    }

    setComputing(true);
    setError(null);
    setResult(null);
    setCurrentCalculationId(null);
    setSelectedRecentId(null);
    setPage(0);
    setPendingEdits(new Map());
    setPersistedEditedCells({});
    setEditError(null);
    setEditedCalcAvailable(false);
    setViewMode("original");
    setModifiedRowsOnly(false);

    try {
      // The /spreadsheet endpoint now returns only metadata (id, sizes,
      // duration) — not the grid itself. We persist the run server-side
      // and then pull the first page the same way we would for any
      // persisted calculation. This keeps large grids out of the response
      // body and matches the Load-from-recent path.
      const slim = await ApiClient.getModelSpreadsheet(
        parseInt(selectedModelId, 10)
      );

      if (!slim?.CalculationDbId) {
        throw new Error(
          "Calculation finished but no CalculationDbId was returned — persistence may be disabled."
        );
      }

      setCurrentCalculationId(slim.CalculationDbId);
      setResultWarningsCount(
        typeof slim.WarningsCount === 'number' ? slim.WarningsCount : 0
      );

      // Immediately pull the first page of the freshly persisted run so the
      // table renders immediately without a separate user action.
      const data = await ApiClient.getModelSpreadsheetRows(slim.CalculationDbId, {
        startRow: 0,
        limit: pageSize,
      });
      setResult({
        CalculationDbId: slim.CalculationDbId,
        ColumnCount: data.ColumnCount,
        RowCount: data.RowCount,
        StartRow: 0,
        EndRow: data.Rows?.length || 0,
        ExecutionDuration: data.ExecutionDuration,
        Columns: data.Columns || [],
        Rows: data.Rows || [],
        OriginalSpreadsheetCalculationId: data.OriginalSpreadsheetCalculationId ?? null,
        ModifiedCells: data.ModifiedCells || {},
      });
      setSelectedRecentId(slim.CalculationDbId);

      // Refresh the recent list so this new run is at the top.
      await loadRecentCalculations(parseInt(selectedModelId, 10));
    } catch (err) {
      console.error("Spreadsheet calculation failed", err);
      const info = {
        message: err.message || "Unknown error",
        structuredErrors: err.structuredErrors || null,
        status: err.status || null,
      };
      setError(info);
    } finally {
      setComputing(false);
    }
  }

  async function loadCalculation(calc) {
    if (!calc || !calc.Id) return;

    setLoading(true);
    setError(null);
    setResult(null);
    setSelectedRecentId(calc.Id);
    setPage(0);
    setPendingEdits(new Map());
    setEditError(null);

    try {
      const data = await ApiClient.getModelSpreadsheetRows(calc.Id, {
        startRow: 0,
        limit: pageSize,
      });
      setResult({
        CalculationDbId: data.CalculationDbId,
        ColumnCount: data.ColumnCount,
        RowCount: data.RowCount,
        StartRow: 0,
        EndRow: data.Rows?.length || 0,
        ExecutionDuration: data.ExecutionDuration,
        Columns: data.Columns || [],
        Rows: data.Rows || [],
        OriginalSpreadsheetCalculationId: data.OriginalSpreadsheetCalculationId ?? null,
        ModifiedCells: data.ModifiedCells || {},
      });
      setCurrentCalculationId(data.CalculationDbId);
      setPersistedEditedCells(data.ModifiedCells || {});
      // The recent-calculations endpoint supplied WarningsCount already; use
      // it instead of forcing a second round-trip to fetch it.
      setResultWarningsCount(
        typeof calc.WarningsCount === 'number' ? calc.WarningsCount : 0
      );

      // If the user just loaded an original row, probe whether they
      // already have a saved edited sibling. The toggle becomes active
      // only when the probe succeeds.
      if (!calc.OriginalSpreadsheetCalculationId) {
        try {
          const edited = await ApiClient.getEditedModelSpreadsheet(calc.Id);
          setEditedCalcAvailable(!!edited);
          setViewMode("original");
          setModifiedRowsOnly(false);
        } catch {
          setEditedCalcAvailable(false);
          setViewMode("original");
        }
      } else {
        // Loaded calc IS the edited sibling.
        setEditedCalcAvailable(false);
        setViewMode("edited");
        setModifiedRowsOnly(false);
      }
    } catch (err) {
      console.error("Loading spreadsheet calculation failed", err);
      const info = {
        message: err.message || "Unknown error",
        structuredErrors: err.structuredErrors || null,
        status: err.status || null,
      };
      setError(info);
    } finally {
      setLoading(false);
    }
  }

  async function loadEditedVersion(originalCalc) {
    if (!originalCalc || !originalCalc.Id) return;

    setLoading(true);
    setError(null);
    setEditError(null);
    setPendingEdits(new Map());

    try {
      const edited = await ApiClient.getEditedModelSpreadsheet(originalCalc.Id);
      if (!edited) {
        setError({
          message: "No saved edits for this calculation yet. Edit a cell and click Save.",
          structuredErrors: null,
          status: null,
        });
        return;
      }
      // Reuse loadCalculation for the actual grid fetch, then flip the
      // view mode and reset pending edits so the orange highlights stay
      // out of the way until the user starts a new edit round.
      await loadCalculation(edited);
      setViewMode("edited");
    } catch (err) {
      console.error("Loading edited sibling failed", err);
      const info = {
        message: err.message || "Unknown error",
        structuredErrors: err.structuredErrors || null,
        status: err.status || null,
      };
      setError(info);
    } finally {
      setLoading(false);
    }
  }

  async function loadOriginalVersion() {
    if (!result?.OriginalSpreadsheetCalculationId) {
      setViewMode("original");
      return;
    }
    const originalId = result.OriginalSpreadsheetCalculationId;
    setLoading(true);
    setError(null);
    setEditError(null);
    setPendingEdits(new Map());
    try {
      const original = await ApiClient.getRecentModelSpreadsheets(
        parseInt(selectedModelId, 10),
        50,
        { includeEdits: true }
      );
      const originalCalc = (original.Items || []).find((c) => c.Id === originalId);
      if (!originalCalc) {
        setError({
          message: `Original calculation #${originalId} could not be located anymore.`,
          structuredErrors: null,
          status: null,
        });
        return;
      }
      await loadCalculation(originalCalc);
      setViewMode("original");
    } catch (err) {
      console.error("Loading original failed", err);
      const info = {
        message: err.message || "Unknown error",
        structuredErrors: err.structuredErrors || null,
        status: err.status || null,
      };
      setError(info);
    } finally {
      setLoading(false);
    }
  }

  async function deleteCalculation(calc) {
    if (!calc || !calc.Id) return;

    // Ask for explicit confirmation so a stray click cannot wipe a run
    // that may have taken minutes to compute.
    const confirmed = window.confirm(
      `Delete calculation #${calc.Id}? This removes the persisted run and all of its cell rows and cannot be undone.`
    );
    if (!confirmed) return;

    setDeletingRecentId(calc.Id);
    setError(null);

    // Optimistically drop the row from the list so the UI feels snappy.
    // If the server call fails we restore the row in the catch below.
    const previousList = recentCalculations;
    setRecentCalculations((prev) => prev.filter((c) => c.Id !== calc.Id));

    try {
      await ApiClient.deleteModelSpreadsheet(calc.Id);

      // If the deleted calc is the one currently rendered, clear the
      // grid so the user does not stare at stale data.
      if (currentCalculationId === calc.Id || selectedRecentId === calc.Id) {
        setResult(null);
        setCurrentCalculationId(null);
        setSelectedRecentId(null);
        setPage(0);
        setResultWarningsCount(0);
        setWarningsModal(null);
      }
    } catch (err) {
      console.error("Deleting spreadsheet calculation failed", err);
      // Restore the row we optimistically removed.
      setRecentCalculations(previousList);
      const info = {
        message: err.message || "Unknown error",
        structuredErrors: err.structuredErrors || null,
        status: err.status || null,
      };
      setError(info);
    } finally {
      setDeletingRecentId(null);
    }
  }

  // Delete the user's edited sibling row for the currently-loaded
  // original and bounce the toolbar back to the original view. The
  // server enforces ownership (only the row's UserId or an admin may
  // delete), so this call is safe to expose without extra client-side
  // authorization.
  async function deleteEditedCopy() {
    // We need the edited sibling's DB id. The currently-loaded
    // calculation IS the edited sibling when viewMode === "edited", so
    // currentCalculationId is the right id to send to the server.
    if (!currentCalculationId) return;
    if (viewMode !== "edited") return;

    const confirmed = window.confirm(
      `Delete your edited copy (calc #${currentCalculationId})? ` +
        `This removes every modified cell you saved and cannot be undone. ` +
        `The original calculation is left untouched.`
    );
    if (!confirmed) return;

    setDeletingEditedId(true);
    setError(null);
    setEditError(null);

    try {
      await ApiClient.deleteModelSpreadsheet(currentCalculationId);

      // Drop the edited sibling from the recent list and from the
      // toolbar state. The simplest correct post-state: no edited copy
      // exists for this original anymore, so clear the persisted-edit
      // dictionary and switch back to original view.
      setRecentCalculations((prev) =>
        (prev || []).filter((c) => c.Id !== currentCalculationId)
      );
      setPersistedEditedCells({});
      setEditedCalcAvailable(false);
      setViewMode("original");
      setModifiedRowsOnly(false);

      // Refresh the recent-calculations list so the server-side state
      // (HasEdits badges, counts) lines up with what we just deleted.
      if (selectedModelId) {
        await loadRecentCalculations(parseInt(selectedModelId, 10));
      }
    } catch (err) {
      console.error("Deleting edited copy failed", err);
      const info = {
        message: err.message || "Unknown error",
        structuredErrors: err.structuredErrors || null,
        status: err.status || null,
      };
      setError(info);
    } finally {
      setDeletingEditedId(false);
    }
  }

  async function fetchPage(newPage) {
    if (!currentCalculationId) return;
    if (newPage < 0 || newPage >= totalPages) return;

    setLoading(true);
    setError(null);
    try {
      const data = await ApiClient.getModelSpreadsheetRows(currentCalculationId, {
        startRow: newPage * pageSize,
        limit: pageSize,
      });
      // Replace Rows with the new page only. We do NOT accumulate rows
      // from previously-visited pages: visibleRows used to slice the
      // accumulated array with `page * pageSize`, which silently produced
      // an empty result whenever the user jumped to a non-zero page
      // (the array never grew long enough to reach that offset).
      // See the comment on `visibleRows` for the bug this used to cause.
      setResult((prev) =>
        prev
          ? {
              ...prev,
              Rows: (data.Rows || []).sort(
                (a, b) => (a.Index ?? 0) - (b.Index ?? 0)
              ),
              OriginalSpreadsheetCalculationId: data.OriginalSpreadsheetCalculationId ?? null,
              ModifiedCells: data.ModifiedCells || {},
            }
          : prev
      );
      setPage(newPage);
      // Refresh the persisted-edit dictionary too so yellow highlights
      // remain accurate as the user paginates.
      if (data.ModifiedCells) {
        setPersistedEditedCells(data.ModifiedCells);
      }
    } catch (err) {
      console.error("Loading spreadsheet page failed", err);
      const info = {
        message: err.message || "Unknown error",
        structuredErrors: err.structuredErrors || null,
        status: err.status || null,
      };
      setError(info);
    } finally {
      setLoading(false);
    }
  }

  function onPageSizeChange(newSize) {
    setPageSize(newSize);
    setPage(0);
    // The currently-rendered page was fetched at the previous page size,
    // so its row count no longer matches what the table expects. Clear it
    // and let the user click a navigation button to load the first page
    // at the new size (consistent with AdminCompareSpreadsheets).
    setResult((prev) => (prev ? { ...prev, Rows: [] } : prev));
  }

  // ----- Cell editing (local buffer + bulk Save) -----
  //
  // We no longer persist edits per-cell. Double-click puts the cell in
  // edit mode; Enter / blur buffers the new value into `pendingEdits`
  // (orange highlight); Save flushes the whole dictionary to the server
  // in one POST and re-renders the recomputed grid; Undo drops the
  // buffer and reverts the cell text.

  function startEdit(rowIdx, colIdx, initialValue) {
    setEditingCell({ rowIdx, colIdx });
    setEditingValue(initialValue);
    setEditError(null);
  }

  function cancelEdit() {
    setEditingCell(null);
    setEditingValue("");
    setEditError(null);
  }

  // Called when the user presses Enter or blurs the <input>. Buffers
  // the edit locally so the cell turns orange without any server hit.
  function commitPendingEdit(columnIndex, rowIndex, newValue) {
    if (!result) return;

    if (columnIndex < 0 || columnIndex >= result.ColumnCount) {
      setEditError(`Column index ${columnIndex} is out of bounds (0-${result.ColumnCount - 1})`);
      return;
    }
    if (rowIndex < 0 || rowIndex >= result.RowCount) {
      setEditError(`Row index ${rowIndex} is out of bounds (0-${result.RowCount - 1})`);
      return;
    }

    const parsed = parseFloat(newValue);
    if (Number.isNaN(parsed)) {
      setEditError("Value must be a valid number");
      return;
    }

    const key = `${columnIndex}:${rowIndex}`;
    const originalText =
      result.Rows?.find((r) => r.Index === rowIndex)?.Cells?.[columnIndex]?.Text ??
      String(newValue);

    setPendingEdits((prev) => {
      const next = new Map(prev);
      const existing = next.get(key);
      // Preserve the originalText if this is a re-edit of a previously
      // committed-but-not-saved cell, otherwise capture it now.
      next.set(key, {
        originalText: existing?.originalText ?? originalText,
        newValue: parsed,
      });
      return next;
    });

    cancelEdit();
    setEditError(null);
  }

  // Drop every buffered edit; useful for the Undo button.
  function undoAllPendingEdits() {
    setPendingEdits(new Map());
    setEditError(null);
  }

  // Bulk-Save: send the entire pending dictionary to the server, get
  // back the slim ack, then refetch the first page of the persisted
  // edited sibling. Works from either view:
  //   * Original view — first save creates the sibling.
  //   * Edited view  — successive saves update the same sibling in place
  //     (the backend drops the previous sibling for this (original, user)
  //     tuple and inserts a fresh one under the same
  //     OriginalSpreadsheetCalculationId, so the pin survives).
  async function saveAllPendingEdits() {
    if (!selectedModelId || !currentCalculationId) return;
    if (pendingEdits.size === 0) return;

    // The original to point the new sibling at is the calculation the
    // user is editing. From Original view this is the loaded original;
    // from Edited view this is the original the sibling points at
    // (carried in `result.OriginalSpreadsheetCalculationId`). Both
    // resolve to the same target — the original's id — so the backend
    // finds and replaces the user's existing edited sibling instead of
    // creating a second one.
    const targetOriginalId =
      result?.OriginalSpreadsheetCalculationId ?? currentCalculationId;

    // Pick a deterministic "last edit" so the backend knows which cell
    // to use as the column+1 restart pivot. We use the most recently
    // committed entry (insertion order — Map preserves it).
    let lastKey = null;
    for (const k of pendingEdits.keys()) {
      lastKey = k;
    }
    if (!lastKey) return;
    const [lastColStr, lastRowStr] = lastKey.split(":");
    const lastEditColumnIndex = parseInt(lastColStr, 10);
    const lastEditRowIndex = parseInt(lastRowStr, 10);
    if (
      Number.isNaN(lastEditColumnIndex) ||
      Number.isNaN(lastEditRowIndex)
    ) {
      setEditError("Internal error: could not determine the most recent edit.");
      return;
    }

    const editsDict = {};
    for (const [key, value] of pendingEdits.entries()) {
      editsDict[key] = value.newValue;
    }

    const wasAlreadyOnEdited = viewMode === "edited";

    // Remember which page the user was looking at so we can re-render
    // the same page after the recalc instead of jumping back to page 1.
    // Recomputation preserves row ordering, so the row index the user was
    // viewing stays at the same offset; we just need to clamp in case
    // RowCount shrinks below the current page's start offset. We use the
    // pre-save RowCount for the clamp — the request payload returns the
    // new RowCount in the same response, so the page indicator (which is
    // derived from `result.RowCount`) will reflect reality on the next
    // render without an extra round-trip.
    const pageToRestore = page;
    const rowCountBefore = result?.RowCount ?? 0;
    const maxPageBefore = Math.max(0, Math.ceil(rowCountBefore / pageSize) - 1);
    const clampedPage = Math.min(pageToRestore, maxPageBefore);
    const startRow = clampedPage * pageSize;

    setSavingAll(true);
    setEditError(null);
    try {
      const slim = await ApiClient.saveSpreadsheetEdits(
        parseInt(selectedModelId, 10),
        targetOriginalId,
        editsDict,
        { columnIndex: lastEditColumnIndex, rowIndex: lastEditRowIndex }
      );

      const newCalcId = slim.CalculationDbId || slim.calculationDbId;
      if (!newCalcId) {
        throw new Error("Save finished but no CalculationDbId was returned.");
      }

      const rowsData = await ApiClient.getModelSpreadsheetRows(newCalcId, {
        startRow,
        limit: pageSize,
      });

      setResult({
        ColumnCount: rowsData.ColumnCount,
        RowCount: rowsData.RowCount,
        StartRow: startRow,
        EndRow: startRow + (rowsData.Rows?.length || 0),
        ExecutionDuration: slim.ExecutionDuration,
        CalculationDbId: newCalcId,
        Columns: rowsData.Columns || [],
        Rows: (rowsData.Rows || []).sort(
          (a, b) => (a.Index ?? 0) - (b.Index ?? 0)
        ),
        OriginalSpreadsheetCalculationId: rowsData.OriginalSpreadsheetCalculationId ?? targetOriginalId,
        ModifiedCells: rowsData.ModifiedCells || {},
      });
      setCurrentCalculationId(newCalcId);
      setPersistedEditedCells(rowsData.ModifiedCells || {});
      setSelectedRecentId(newCalcId);
      setPendingEdits(new Map());
      setPage(clampedPage);
      setEditedCalcAvailable(true);
      // If the user was on Original view, this is their first save →
      // jump to the edited view to show the recomputed grid. If they
      // were already on Edited view, stay there so they can keep editing.
      setViewMode("edited");
      setModifiedRowsOnly(false);
      void wasAlreadyOnEdited; // referenced for clarity; currently unused.

      // Refresh the recent-calculations list so the edited sibling shows up.
      await loadRecentCalculations(parseInt(selectedModelId, 10));
    } catch (err) {
      console.error("Failed to save spreadsheet edits", err);
      setEditError(
        `Failed to save edits: ${err.message || "Unknown error"}`
      );
    } finally {
      setSavingAll(false);
    }
  }

  function handleCellKeyDown(e, columnIndex, rowIndex) {
    if (e.key === "Enter") {
      e.preventDefault();
      commitPendingEdit(columnIndex, rowIndex, editingValue);
    } else if (e.key === "Escape") {
      e.preventDefault();
      cancelEdit();
    }
  }

  const selectedModel = useMemo(
    () => models.find((m) => m.Id?.toString() === selectedModelId) || null,
    [models, selectedModelId]
  );

  return (
    <div>
      <h1>Model spreadsheet</h1>
      <p className="text-muted">
        Runs the selected model on the internal compute engine using the same
        data preparation as internal model execution and renders the entire
        computed grid (every column and every row), not only the extracted
        signals. Calculations are persisted so you can reload a previous run
        without re-running the study.
      </p>

      <div className="card mb-4">
        <div className="card-header">
          <h5 className="mb-0">Calculate spreadsheet</h5>
        </div>
        <div className="card-body">
          <div className="row">
            <div className="col-md-6">
              <label className="form-label">Model</label>
              <select
                className="form-control"
                value={selectedModelId}
                onChange={(e) => setSelectedModelId(e.target.value)}
                disabled={computing || loading}
              >
                <option value="">-- Select a model --</option>
                {models.map((m) => (
                  <option key={m.Id} value={m.Id}>
                    {m.Name || m.FileName || `Model #${m.Id}`} (ID: {m.Id})
                  </option>
                ))}
              </select>
              {selectedModel && (
                <small className="text-muted">
                  File: {selectedModel.FileName || "—"} · Size:{" "}
                  {selectedModel.getFormattedFileSize?.() || "—"}
                </small>
              )}
              {result && (
                <div className="text-muted mt-2">
                  Last run: {formatDuration(result.ExecutionDuration)} ·{" "}
                  {result.ColumnCount} columns × {result.RowCount} rows
                  {result.CalculationDbId && (
                    <> · Calc #{result.CalculationDbId}</>
                  )}
                </div>
              )}
            </div>
          </div>

          {error && (
            <div className="alert alert-danger mt-3">
              <h6>Calculation error</h6>
              {error.structuredErrors && error.structuredErrors.length > 0 ? (
                error.structuredErrors.map((e, i) => (
                  <div key={i}>
                    <strong>Error #{e.Code || 0}:</strong> {e.Message}
                  </div>
                ))
              ) : (
                <div>{error.message}</div>
              )}
              {error.status && (
                <small className="text-muted">HTTP Status: {error.status}</small>
              )}
            </div>
          )}
        </div>
        <div className="card-footer d-flex justify-content-end">
          <button
            className="btn btn-primary"
            onClick={calculateNew}
            disabled={computing || loading || !selectedModelId}
          >
            {computing ? "Calculating…" : "Calculate"}
          </button>
        </div>
      </div>

      {/* Recent calculations — pick one and click Load instead of Calculate New */}
      {selectedModelId && (
        <div className="card mb-4">
          <div className="card-header d-flex justify-content-between align-items-center">
            <h5 className="mb-0">Recent calculations</h5>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={() => loadRecentCalculations(parseInt(selectedModelId, 10))}
              disabled={loadingRecent}
            >
              {loadingRecent ? "Refreshing…" : "Refresh"}
            </button>
          </div>
          <div className="card-body p-0">
            {loadingRecent ? (
              <div className="p-3 text-muted">Loading recent calculations…</div>
            ) : recentCalculations.length === 0 ? (
              <div className="p-3 text-muted">
                No previous calculations for this model. Click{" "}
                <em>Calculate New</em> to start one.
              </div>
            ) : (
              <table className="table table-sm table-hover mb-0 model-spreadsheet-recent-table">
                <thead>
                  <tr>
                    <th style={{ width: 60 }}>Load</th>
                    <th style={{ width: 80 }}>Delete</th>
                    <th>Calc #</th>
                    <th>Created</th>
                    <th>Data source</th>
                    <th>Duration</th>
                    <th>Columns × Rows</th>
                    <th>Edits</th>
                    <th>Warnings</th>
                  </tr>
                </thead>
                <tbody>
                  {recentCalculations.map((calc) => {
                    const isSelected = selectedRecentId === calc.Id;
                    const isDeleting = deletingRecentId === calc.Id;
                    const dataSource = calc.DataSource || "DB";
                    const isOriginal = dataSource === "OriginalCTRAC";
                    const warningsCount =
                      typeof calc.WarningsCount === 'number' ? calc.WarningsCount : 0;
                    const dataSourceLabel = isOriginal ? 'OriginalCTRAC' : 'DB';
                    return (
                      <tr
                        key={calc.Id}
                        className={isSelected ? "table-active" : undefined}
                      >
                        <td>
                          <button
                            type="button"
                            className="btn btn-sm btn-success"
                            disabled={loading || isDeleting}
                            onClick={() => loadCalculation(calc)}
                          >
                            Load
                          </button>
                        </td>
                        <td>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            disabled={isDeleting || loading}
                            onClick={() => deleteCalculation(calc)}
                            title={`Delete calculation #${calc.Id}`}
                          >
                            {isDeleting ? "Deleting…" : "Delete"}
                          </button>
                        </td>
                        <td>#{calc.Id}</td>
                        <td>{formatDateTime(calc.CreateTime)}</td>
                        <td>
                          <span
                            className={`badge badge-${isOriginal ? "info" : "primary"}`}
                            style={{ color: "#000" }}
                          >
                            {isOriginal ? "Original CTRAC" : "DB"}
                          </span>
                        </td>
                        <td>{formatDuration(calc.ExecutionDuration)}</td>
                        <td>
                          {calc.ColumnCount} × {calc.RowCount}
                        </td>
                        <td>
                          {calc.HasEdits ? (
                            <span
                              className="badge badge-warning"
                              title={`Edited copy of calculation #${calc.OriginalSpreadsheetCalculationId}`}
                              style={{ color: "#000" }}
                            >
                              Edited ({calc.ModifiedCellsCount})
                            </span>
                          ) : editedCalcAvailable && calc.Id === selectedRecentId && viewMode === "edited" ? null : (
                            <span className="text-muted">—</span>
                          )}
                        </td>
                        <td>
                          {warningsCount > 0 ? (
                            <button
                              type="button"
                              className="removed-rows-warning-icon-btn"
                              onClick={() =>
                                setWarningsModal({
                                  calculationDbId: calc.Id,
                                  calculationTitle: `Calculation #${calc.Id}`,
                                  dataSourceLabel,
                                })
                              }
                              title={`Calculation ${calc.Id} produced ${warningsCount} warning${warningsCount === 1 ? '' : 's'}. Click to see details.`}
                              aria-label={`Calculation ${calc.Id} produced ${warningsCount} warning${warningsCount === 1 ? '' : 's'}. Click to see details.`}
                            >
                              <span
                                className="removed-rows-warning-icon"
                                role="img"
                                aria-hidden="true"
                              >
                                ⚠
                              </span>
                              <span className="removed-rows-warning-count">
                                {warningsCount}
                              </span>
                            </button>
                          ) : (
                            <span className="text-muted">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* Shares — only shown once a model is selected. Columns are sourced
          from the latest computed spreadsheet so users can pick the same
          labels they see in the table. The viewMode + currentCalculationId
          props let ModelShares auto-fill the "Only this exactly calculation"
          pin field when the user is in Edited mode and wants to share
          that exact edited copy. */}
      {selectedModelId && (
        <ModelShares
          selectedModelId={selectedModelId}
          latestColumns={result?.Columns || []}
          viewMode={viewMode}
          currentCalculationId={
            viewMode === "edited"
              ? currentCalculationId
              : (result?.CalculationDbId || currentCalculationId)
          }
        />
      )}

      {loading && !result && (
        <div className="alert alert-info">Loading…</div>
      )}

      {editError && (
        <div className="alert alert-danger mt-2">
          <strong>Edit error:</strong> {editError}{" "}
          <button
            type="button"
            className="btn btn-sm btn-link"
            onClick={() => setEditError(null)}
          >
            Dismiss
          </button>
        </div>
      )}

      {result && result.Columns && result.Columns.length > 0 && (
        <div className="card">
          <div className="card-header d-flex justify-content-between align-items-center flex-wrap">
            <h5 className="mb-0 d-inline-flex align-items-center flex-wrap">
              Computed spreadsheet
              {result.CalculationDbId && (
                <small className="text-muted ml-2">
                  · Calc #{result.CalculationDbId}
                </small>
              )}
              {resultWarningsCount > 0 && (
                <button
                  type="button"
                  className="removed-rows-warning-icon-btn ml-2"
                  onClick={() =>
                    setWarningsModal({
                      calculationDbId: result.CalculationDbId || currentCalculationId,
                      calculationTitle: `Calc #${result.CalculationDbId || currentCalculationId}`,
                      dataSourceLabel: result.DataSource || 'DB',
                    })
                  }
                  title={`Calculation produced ${resultWarningsCount} warning${resultWarningsCount === 1 ? '' : 's'}. Click to see details.`}
                  aria-label={`Calculation produced ${resultWarningsCount} warning${resultWarningsCount === 1 ? '' : 's'}. Click to see details.`}
                >
                  <span
                    className="removed-rows-warning-icon"
                    role="img"
                    aria-hidden="true"
                  >
                    ⚠
                  </span>
                  <span className="removed-rows-warning-count">
                    {resultWarningsCount}
                  </span>
                </button>
              )}

              {/* Original / Edited toggle. Visible whenever the user has a
                  saved edited sibling of the currently-loaded original (or
                  whenever the currently-loaded calc IS the edited sibling).
                  Switching is driven by dedicated buttons that fetch the
                  sibling via the existing /spreadsheet/{id}/edited endpoint. */}
              {(editedCalcAvailable || result.OriginalSpreadsheetCalculationId != null) && (
                <div className="btn-group btn-group-sm ml-3 model-spreadsheet-view-toggle">
                  <button
                    type="button"
                    className={`btn model-spreadsheet-toggle-btn ${viewMode === "original" ? "model-spreadsheet-toggle-btn-active" : "btn-outline-secondary"}`}
                    disabled={viewMode === "original" || loading}
                    onClick={() => loadOriginalVersion()}
                    title="Show the unmodified original calculation"
                  >
                    Original
                  </button>
                  <button
                    type="button"
                    className={`btn model-spreadsheet-toggle-btn ${viewMode === "edited" ? "model-spreadsheet-toggle-btn-active" : "btn-outline-secondary"}`}
                    disabled={
                      viewMode === "edited" ||
                      loading ||
                      (!editedCalcAvailable && !result.OriginalSpreadsheetCalculationId)
                    }
                    onClick={() =>
                      result.OriginalSpreadsheetCalculationId
                        ? loadOriginalVersion().then(() =>
                            loadEditedVersion({
                              Id: result.OriginalSpreadsheetCalculationId,
                            })
                          )
                        : loadEditedVersion({
                            Id: result.CalculationDbId || currentCalculationId,
                          })
                    }
                    title="Show the saved edited copy (yellow cells)"
                  >
                    Edited
                  </button>
                </div>
              )}

              {/* Show all / Show only modified rows filter. Only meaningful
                  in the edited view, where the persisted edited cells are
                  known. Hidden on the original view to keep the toolbar
                  uncluttered. */}
              {viewMode === "edited" && (
                <div className="btn-group btn-group-sm ml-2 model-spreadsheet-modified-toggle">
                  <button
                    type="button"
                    className={`btn model-spreadsheet-toggle-btn ${!modifiedRowsOnly ? "model-spreadsheet-toggle-btn-active" : "btn-outline-secondary"}`}
                    onClick={() => setModifiedRowsOnly(false)}
                  >
                    Show all
                  </button>
                  <button
                    type="button"
                    className={`btn model-spreadsheet-toggle-btn ${modifiedRowsOnly ? "model-spreadsheet-toggle-btn-active" : "btn-outline-secondary"}`}
                    onClick={() => setModifiedRowsOnly(true)}
                    title="Only show rows that contain at least one edited cell"
                  >
                    Show only modified rows
                  </button>
                </div>
              )}

              {/* Modified-cells badge — always visible whenever the
                  currently loaded calculation has a non-empty persisted
                  dictionary. Gives the user a concrete confirmation that
                  the dictionary was loaded from the server (and tells them
                  how many cells to expect to see highlighted). Rendered in
                  both the original and edited views so a user can verify
                  the persisted dictionary even before clicking Edited. */}
              {Object.keys(persistedEditedCells || {}).length > 0 && (
                <span
                  className="badge ml-2 model-spreadsheet-modified-count-badge"
                  title="Number of cells in the persisted edit dictionary for the loaded calculation"
                >
                  {Object.keys(persistedEditedCells).length} modified
                </span>
              )}

              {/* Save / Undo buttons. Shown whenever the user has at
                  least one buffered (orange) edit — in either view.
                  From Original view, the first Save creates the edited
                  sibling. From Edited view, subsequent Saves update the
                  same sibling in place (the backend drops the previous
                  sibling row and inserts a fresh one under the same
                  OriginalSpreadsheetCalculationId, so the pin
                  survives). */}
              {pendingEdits.size > 0 && (
                <div className="btn-group btn-group-sm ml-3 model-spreadsheet-edit-actions">
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={savingAll || loading || computing}
                    onClick={saveAllPendingEdits}
                    title={
                      viewMode === "edited"
                        ? "Update your edited copy with the latest pending edits (same calc id)"
                        : "Persist all pending edits, recompute the grid, and switch to the edited view"
                    }
                  >
                    {savingAll ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm mr-1"
                          role="status"
                          aria-hidden="true"
                        ></span>
                        Saving…
                      </>
                    ) : (
                      <>Save ({pendingEdits.size})</>
                    )}
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    disabled={savingAll || loading || computing}
                    onClick={undoAllPendingEdits}
                    title="Drop all pending (orange) edits"
                  >
                    Undo
                  </button>
                </div>
              )}

              {/* Delete edited copy — only visible while the user is
                  looking at their edited sibling. Hits
                  DELETE /api/spreadsheets/{id}, which the server scopes
                  to the row's owner or an admin, so this only ever
                  removes the calling user's edited copy. The original
                  calculation is left untouched on the server. */}
              {viewMode === "edited" && currentCalculationId && (
                <div className="btn-group btn-group-sm ml-3 model-spreadsheet-edit-actions">
                  <button
                    type="button"
                    className="btn btn-outline-danger"
                    disabled={deletingEditedId || loading || computing}
                    onClick={deleteEditedCopy}
                    title="Delete your edited copy of this calculation (the original is preserved)"
                  >
                    {deletingEditedId ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm mr-1"
                          role="status"
                          aria-hidden="true"
                        ></span>
                        Deleting…
                      </>
                    ) : (
                      <>Delete edited copy</>
                    )}
                  </button>
                </div>
              )}
            </h5>
            <div className="d-flex align-items-center">
              <span className="me-2">Rows per page:</span>
              <select
                className="form-control form-control-sm"
                style={{ width: 100 }}
                value={pageSize}
                onChange={(e) => onPageSizeChange(parseInt(e.target.value, 10))}
                disabled={loading}
              >
                {pageSizeOptions.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="card-body p-0">
            <div className="model-spreadsheet-scroll">
              <table className="table table-sm table-bordered mb-0 model-spreadsheet-table">
                <thead>
                  <tr>
                    <th className="row-index-col">#</th>
                    {result.Columns.map((col) => {
                      // The server substitutes the placeholder string in
                      // place of the real formula for non-admins. Treat
                      // the placeholder as "no formula" everywhere we
                      // render from the payload — including the
                      // column-header tooltip — so the formula never
                      // appears in the tooltip for non-admins even if
                      // the payload happens to be stale.
                      const rawFormula = col.Formula;
                      const hasRealFormula =
                        !!rawFormula &&
                        rawFormula !== HIDDEN_FORMULA_PLACEHOLDER &&
                        isAdmin;
                      const tagValue = col.Tag;
                      // Title shown on hover. Kept identical to before
                      // for admins so existing muscle memory still works;
                      // non-admins simply see the Tag line and never the
                      // formula line.
                      const tooltipTitle = `${col.Label}${col.Title ? " · " + col.Title : ""}${
                        hasRealFormula ? "\n" + rawFormula : ""
                      }${tagValue ? "\nTag: " + tagValue : ""}`;
                      return (
                        <th key={col.Index} title={tooltipTitle}>
                          <div className="model-spreadsheet-col-header">
                            <span className="model-spreadsheet-col-label">
                              {col.Label}
                            </span>
                            {col.Title && (
                              <span className="model-spreadsheet-col-title">
                                {col.Title}
                              </span>
                            )}
                            {tagValue && (
                              <span className="badge badge-info model-spreadsheet-col-tag">
                                {tagValue}
                              </span>
                            )}
                          </div>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {visibleRows.map((row) => (
                    <tr key={row.Index}>
                      <td className="row-index-col">{row.Index + 1}</td>
                      {(row.Cells || []).map((cell, ci) => {
                        const bg = colorToCss(cell.Color);
                        const isEditing = editingCell?.rowIdx === row.Index && editingCell?.colIdx === ci;
                        const pendingKey = `${ci}:${row.Index}`;
                        const pending = pendingEdits.get(pendingKey);
                        const persistedValue = Object.prototype.hasOwnProperty.call(
                          persistedEditedCells || {},
                          pendingKey
                        )
                          ? persistedEditedCells[pendingKey]
                          : undefined;
                        // Class precedence:
                        //   * persistedEdited — yellow highlight (edited view)
                        //   * pending         — orange highlight (any view)
                        // When a cell is both persisted (previously saved) and
                        // has a fresh pending edit, we still apply BOTH classes
                        // but list pending AFTER persisted so the orange variant
                        // wins the CSS cascade. The base
                        // model-spreadsheet-cell class carries no background —
                        // we move the format-color fill from the <td> inline
                        // style down into the inner <span> so the highlight
                        // classes can own the <td>'s background + border
                        // without inline-style competition. This makes the
                        // yellow / orange highlight visible on format-87 colored
                        // cells (Buy/Sell) too.
                        let cellClass = "model-spreadsheet-cell";
                        if (persistedValue !== undefined) cellClass += " model-spreadsheet-cell-persisted";
                        if (pending) cellClass += " model-spreadsheet-cell-pending";
                        return (
                          <td
                            key={ci}
                            className={cellClass}
                          >
                            {isEditing ? (
                              <input
                                type="text"
                                className="model-spreadsheet-cell-edit-input"
                                value={editingValue}
                                onChange={(e) => setEditingValue(e.target.value)}
                                onBlur={() => commitPendingEdit(ci, row.Index, editingValue)}
                                onKeyDown={(e) => handleCellKeyDown(e, ci, row.Index)}
                                autoFocus
                                disabled={savingAll}
                              />
                            ) : (
                              <span
                                className={savingAll ? "model-spreadsheet-cell-saving" : undefined}
                                onDoubleClick={() => startEdit(row.Index, ci, cell.Text)}
                                title={
                                  pending
                                    ? "Pending edit (Save to commit)"
                                    : persistedValue !== undefined
                                    ? `Persisted edit (value: ${persistedValue})`
                                    : "Double-click to edit"
                                }
                                style={bg ? { backgroundColor: bg } : undefined}
                              >
                                {pending ? String(pending.newValue) : cell.Text}
                              </span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                  {visibleRows.length === 0 && (
                    <tr>
                      <td
                        colSpan={result.Columns.length + 1}
                        className="text-center text-muted"
                      >
                        {loading ? "Loading…" : "No rows in the returned slice."}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
          <div className="card-footer d-flex justify-content-between align-items-center flex-wrap">
            <div>
              Page {page + 1} of {totalPages} ({totalRows} rows)
              {loading && (
                <span className="ms-2 text-muted">Loading…</span>
              )}
            </div>
            <div className="btn-group">
              <button
                className="btn btn-sm btn-outline-secondary"
                disabled={page === 0 || loading}
                onClick={() => fetchPage(0)}
              >
                «
              </button>
              <button
                className="btn btn-sm btn-outline-secondary"
                disabled={page === 0 || loading}
                onClick={() => fetchPage(Math.max(0, page - 1))}
              >
                ‹ Prev
              </button>
              <button
                className="btn btn-sm btn-outline-secondary"
                disabled={page >= totalPages - 1 || loading}
                onClick={() => fetchPage(Math.min(totalPages - 1, page + 1))}
              >
                Next ›
              </button>
              <button
                className="btn btn-sm btn-outline-secondary"
                disabled={page >= totalPages - 1 || loading}
                onClick={() => fetchPage(totalPages - 1)}
              >
                »
              </button>
            </div>
            <div className="d-flex align-items-center mt-2 mt-md-0 ms-md-2">
              <label
                htmlFor="model-spreadsheet-page-jump"
                className="text-muted small me-2 mb-0"
              >
                Go to page
              </label>
              <input
                id="model-spreadsheet-page-jump"
                type="number"
                min={1}
                max={totalPages}
                step={1}
                className="form-control form-control-sm"
                style={{ width: '5rem' }}
                value={pageInputValue}
                disabled={loading || totalPages <= 1}
                onChange={(e) => setPageInputValue(e.target.value)}
                onBlur={commitPageInput}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    commitPageInput();
                  }
                }}
                aria-label={`Jump to page (1 to ${totalPages})`}
                title={`Enter a page number between 1 and ${totalPages}`}
              />
              <span className="text-muted small ms-2">/ {totalPages}</span>
            </div>
          </div>
        </div>
      )}

      {result && (!result.Columns || result.Columns.length === 0) && (
        <div className="alert alert-warning">
          Model execution succeeded but the computed study contains no columns.
        </div>
      )}

      {warningsModal && (
        <WarningsModal
          isOpen={!!warningsModal}
          toggle={() => setWarningsModal(null)}
          calculationDbId={warningsModal.calculationDbId}
          calculationTitle={warningsModal.calculationTitle}
          dataSourceLabel={warningsModal.dataSourceLabel}
        />
      )}
    </div>
  );
};

function formatDuration(duration) {
  if (duration == null) return "";
  if (typeof duration === "string") {
    // TimeSpan string "hh:mm:ss.fffffff"
    const parts = duration.split(":");
    if (parts.length >= 3) {
      const hours = parseInt(parts[0], 10) || 0;
      const minutes = parseInt(parts[1], 10) || 0;
      const seconds = parseFloat(parts[2]) || 0;
      if (hours > 0) return `${hours}h ${minutes}m ${seconds.toFixed(1)}s`;
      if (minutes > 0) return `${minutes}m ${seconds.toFixed(1)}s`;
      if (seconds >= 1) return `${seconds.toFixed(2)}s`;
      return `${Math.round(seconds * 1000)}ms`;
    }
    return duration;
  }
  if (typeof duration === "number") {
    // Assume milliseconds when numeric
    const totalMs = duration;
    if (totalMs >= 60_000) return `${(totalMs / 60_000).toFixed(1)} min`;
    if (totalMs >= 1_000) return `${(totalMs / 1_000).toFixed(2)}s`;
    return `${Math.round(totalMs)}ms`;
  }
  // TimeSpan-like object with TotalMilliseconds
  if (typeof duration === "object" && typeof duration.TotalMilliseconds === "number") {
    const totalMs = duration.TotalMilliseconds;
    if (totalMs >= 60_000) return `${(totalMs / 60_000).toFixed(1)} min`;
    if (totalMs >= 1_000) return `${(totalMs / 1_000).toFixed(2)}s`;
    return `${Math.round(totalMs)}ms`;
  }
  return String(duration);
}

function formatDateTime(value) {
  if (value == null) return "—";
  // ISO string from JSON — display in local time.
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    return d.toLocaleString();
  } catch {
    return String(value);
  }
}

export default ModelSpreadsheet;
